import { API } from "aws-amplify";
import { useEffect, useState } from "react";
import { useCreateApiCall } from "./useCreateApiCall";

export const useRecipientSelectorsOptions = ({ region, recipient, practice, provider, specialist }) => {
  const { createApiCall } = useCreateApiCall();
  /** @type {import("types/commonTypes").Organization[]} */
  const initialState = [];
  /** @type {import("types/commonTypes").Specialist[]} */
  const sendersInitialState = [];

  const [regions, setRegions] = useState([]);
  const [specialists, setSpecialists] = useState(sendersInitialState);
  const [practices, setPractices] = useState(initialState);
  const [providers, setProviders] = useState(initialState);
  const [senders, setSenders] = useState(sendersInitialState);
  const [ isLoading, setLoading ] = useState(false);

  const regionsOptions = regions.reduce(
    (acc, { id, label }) => ({ ...acc, [id]: label }),
    {}
  );

  const specialistsOptions = specialists.reduce(
    (acc, { id, firstName, lastName, role, phone, email }) => {
      const details = [`${firstName} ${lastName}`, role, phone, email]
        .filter((value) => !!value)
        .join(", ");
      return { ...acc, [id]: details };
    },
    {}
  );

  const practicesOptions = practices.reduce(
    (acc, { id, name, speciality, phone }) => {
      const details = [name, speciality, phone]
        .filter((value) => !!value)
        .join(", ");
      return { ...acc, [id]: details };
    },
    {}
  );

  const providersOptions = providers.reduce(
    (acc, { id, name, speciality }) => {
      const details = [ name, speciality]
        .filter((value) => !!value)
        .join(", ");
      return { ...acc, [id]: details };
    },
    {}
  );

  const sendersOptions = senders.reduce(
    (acc, { id, firstName, lastName, jobTitle, phone, email }) => {
      const details = [`${firstName} ${lastName}`, jobTitle, phone, email]
        .filter((value) => !!value)
        .join(", ");
      return { ...acc, [id]: details };
    },
    {}
  );

  useEffect(() => {
    const getData = createApiCall(
      async () => {
        setLoading(true);
        const result = await API.post("restApi", "/recipient_selectors", {
          body: {
            recipientId: recipient,
            regionId: region,
            practiceId: practice,
            providerId: provider,
            specialistId: specialist,
          },
        });
        setRegions(result?.regions ?? []);
        setPractices(result?.practices ?? []);
        setProviders(result?.providers ?? []);
        setSenders(result?.senders ?? []);
        setSpecialists(result?.specialists ?? []);
        setLoading(false);
      },
      {
        onError: () => {
          setPractices(initialState);
          setSenders(sendersInitialState);
          setSpecialists(sendersInitialState);
          setProviders(initialState);
          setLoading(false);
        },
      }
    );

    const timeout = setTimeout(() => getData(), 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, recipient, practice, provider, specialist]);

  return {
    regionsOptions,
    regions,
    practicesOptions,
    practices,
    providersOptions,
    providers,
    senders,
    sendersOptions,
    isLoading,
    specialistsOptions,
    specialists,
  };
};
